import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { rxResource } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import type {
  ApiKeyListItem,
  ApiKeyCreatedItem,
  ApiKeyDetailsItem,
  ApiKeyPermissionItem,
  CreateApiKey,
  UpdateApiKey,
} from '@zag/auth/models';

export type { ApiKeyListItem, ApiKeyCreatedItem, ApiKeyDetailsItem, ApiKeyPermissionItem } from '@zag/auth/models';

/**
 * Frontend service for the authenticated user's own API keys.
 *
 * Provides a reactive list resource and imperative mutation methods.
 * All endpoints are owner-scoped: only the current user's keys are returned,
 * and only session-authenticated callers may use them (the backend rejects
 * API-key-authenticated calls with HTTP 403).
 *
 * The list endpoint returns `{ data: ApiKeyListItem[] }` (wrapped). All other
 * endpoints return their payload directly.
 */
@Injectable({ providedIn: 'root' })
export class ApiKeyService {
  private readonly http = inject(HttpClient);
  private readonly basePath = '/api/v1/users/me/api-keys';

  // ============================================================================
  // Reactive list resource
  // ============================================================================

  private readonly refreshTrigger = signal(0);

  private readonly keysResource = rxResource<ApiKeyListItem[], void>({
    params: () => ({ _trigger: this.refreshTrigger() }),
    stream: () => this.fetchKeys(),
  });

  readonly keys = () => this.keysResource.value() ?? [];
  readonly isLoading = () => this.keysResource.isLoading();
  readonly error = () => this.keysResource.error();

  reload(): void {
    this.refreshTrigger.update(v => v + 1);
  }

  private fetchKeys(): Observable<ApiKeyListItem[]> {
    return new Observable<ApiKeyListItem[]>(subscriber => {
      const subscription = this.http.get<{ data: ApiKeyListItem[] }>(this.basePath).subscribe({
        next: response => subscriber.next(response.data),
        error: err => subscriber.error(err),
        complete: () => subscriber.complete(),
      });
      return () => subscription.unsubscribe();
    });
  }

  // ============================================================================
  // Mutations
  // ============================================================================

  /**
   * Create a new API key.
   * The response includes the plaintext `key` — only available in this response.
   */
  create(data: CreateApiKey): Observable<ApiKeyCreatedItem> {
    return this.http.post<ApiKeyCreatedItem>(this.basePath, data);
  }

  /**
   * Update an API key's name, enabled status, or permission allowlist.
   */
  update(id: string, patch: UpdateApiKey): Observable<void> {
    return this.http.patch<void>(`${this.basePath}/${encodeURIComponent(id)}`, patch);
  }

  /**
   * Permanently delete an API key.
   */
  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.basePath}/${encodeURIComponent(id)}`);
  }

  /**
   * Returns the full edit view (current restriction state + selected permission ids)
   * for a single API key owned by the current user.
   */
  getDetail(id: string): Observable<ApiKeyDetailsItem> {
    return this.http.get<ApiKeyDetailsItem>(`${this.basePath}/${encodeURIComponent(id)}`);
  }

  /**
   * Returns the permissions the current user is allowed to attach to one of
   * their own API keys (caller's effective permission set).
   */
  listAssignablePermissions(): Observable<ApiKeyPermissionItem[]> {
    return new Observable<ApiKeyPermissionItem[]>(subscriber => {
      const subscription = this.http.get<{ data: ApiKeyPermissionItem[] }>(`${this.basePath}/assignable-permissions`).subscribe({
        next: response => subscriber.next(response.data),
        error: err => subscriber.error(err),
        complete: () => subscriber.complete(),
      });
      return () => subscription.unsubscribe();
    });
  }
}
